import type { PurchasePointsChannel } from "@/features/purchasePoints/types/purchasePointsOffer.type";

// Shared by the catalogue page and the Ma carte widget, which must name the
// page its link opens.
export const PURCHASE_POINTS_TITLE = "Mes Yeaties";
export const PURCHASE_POINTS_INTRODUCTION = "Retrouvez les enseignes chez lesquelles vos achats réglés avec votre carte vous rapportent des yeaties.";

/**
 * The programme is entered on request, so an employee who does not take part is
 * served the same denial as a works council that never opened the feature.
 * This wording survives for the one case the page cannot
 * decide on its own: the switch turned off in another tab while this one was
 * open, which the API answers with `employee_opted_out`. Leaving the programme
 * is a double renunciation -- no more yeaties earned at the till, and no more
 * use of those already held -- so the message states both: keeping the second
 * one quiet would leave him believing he can still spend what he has.
 */
export const PURCHASE_POINTS_OPTED_OUT_MESSAGE = "Vous avez désactivé la fonctionnalité « Mes Yeaties ». Vos achats effectués avec votre carte Yeati ne vous rapportent plus de yeaties, et vos yeaties ne sont plus utilisables.";
export const PURCHASE_POINTS_OPTED_OUT_ACTION_LABEL = "Réactiver dans mes autorisations";
export const PURCHASE_POINTS_ACCOUNT_LOCKED_MESSAGE = "Votre compte Yeati n'est plus actif, « Mes Yeaties » ne vous est plus accessible";
export const PURCHASE_POINTS_EMPLOYEE_DEPARTED_MESSAGE = "« Mes Yeaties » ne vous est plus accessible depuis votre départ de l'entreprise";

export const PURCHASE_POINTS_PATH = "/yeaties";

// One frame for every page of the feature, so moving between them never shifts
// the content.
export const PURCHASE_POINTS_PAGE_LAYOUT = "flex flex-col gap-6 p-4 sm:gap-8 sm:p-6";

export const PURCHASE_POINTS_BRANDS_PATH = "/yeaties/enseignes";
export const PURCHASE_POINTS_BRAND_PATH = "/yeaties/enseignes/:brandSlug";

// The slug the API builds from our own id, never the provider's brand id.
export const buildBrandPath = (slug: string) => `${PURCHASE_POINTS_BRANDS_PATH}/${encodeURIComponent(slug)}`;

export const PURCHASE_POINTS_OFFER_PATH = "/yeaties/enseignes/:brandSlug/offres/:offerId";

// The provider's offer id is an opaque string: encoded so that a reserved
// character in it cannot break out of the path segment.
export const buildOfferPath = (brandSlug: string, offerId: string) =>
  `${buildBrandPath(brandSlug)}/offres/${encodeURIComponent(offerId)}`;

// "Accueil" is not among them: the shared Breadcrumb prepends it on every page.
export const PURCHASE_POINTS_BREADCRUMB_LABELS = {
  yeaties: PURCHASE_POINTS_TITLE,
  brands: "Enseignes",
  offer: "Offre",
} as const;

export const OFFER_HOW_TO_TITLE = "Comment profiter de cette offre";
export const OFFER_HOW_TO_STEPS = {
  pay: (brandName: string) => `Réglez votre achat chez ${brandName} avec votre carte Yeati.`,
  detection: "Si votre achat respecte les conditions de l'offre, vos yeaties sont détectés automatiquement : rien à activer, rien à scanner.",
  availability: "Ils apparaissent d'abord en attente, puis rejoignent vos yeaties disponibles, convertibles en chèque cadeau dans la boutique Yeati.",
} as const;

export const OFFER_CONSUMED_BADGE_LABEL = "Déjà utilisée";
export const OFFER_BOOSTED_BADGE_LABEL = "Taux boosté";
export const OFFER_MERCHANT_LINK_NEW_TAB_HINT = "(nouvel onglet)";
export const buildMerchantLinkLabel = (brandName: string) => `Voir le site de ${brandName}`;
export const OFFER_LOADING_LABEL = "Chargement de l'offre";
export const OFFER_BACK_TO_BRANDS_LABEL = "Voir les enseignes";

export const PURCHASE_POINTS_PER_PAGE = 12;

// Shorter than the catalogue grid: the history is a list under it, and a long
// one would push the page it belongs to out of reach.
export const PURCHASE_POINTS_REWARDS_PER_PAGE = 10;

// A preview, not a duplicate of the history: the full ten gains stay on the
// dedicated page, this one only teases the most recent five.
export const LATEST_REWARDS_COUNT = 3;
export const LATEST_REWARDS_TITLE = "Mes derniers gains";
export const PURCHASE_POINTS_SUMMARY_LINK_LABEL = "Voir Mes Yeaties";

// Said in place of two zeros, which would read as a loss rather than as an
// absence of gain yet.
export const PURCHASE_POINTS_NONE_LABEL = "Aucun yeaties";

export const PURCHASE_POINTS_AVAILABLE_LABEL = "Disponibles";
export const PURCHASE_POINTS_PENDING_LABEL = "En attente";
export const PURCHASE_POINTS_EXPIRED_LABEL = "Expirés";
export const PURCHASE_POINTS_UNIT = "yeaties";

/**
 * Query scopes, shared by the readings and by the invalidations that follow a
 * conversion. A literal typed twice would invalidate nothing and leave a stale
 * screen behind, without failing anywhere.
 */
export const PURCHASE_POINTS_BALANCE_SCOPE = "purchasePointsBalance";
export const PURCHASE_POINTS_CONVERSIONS_SCOPE = "purchasePointsConversions";

// Constant on purpose: the carousel is not paginated, so a page number in its
// key would only fragment its cache across pages of the brand grid below it.
export const PURCHASE_POINTS_HIGHLIGHTED_OFFERS_SCOPE = "purchasePointsHighlightedOffers";

// As long as the rewards history: the two lists sit one under the other.
export const PURCHASE_POINTS_CONVERSIONS_PER_PAGE = 10;

/**
 * The amount always arrives in cents, and the division is never written here:
 * the server owns the rate, and writing it twice would be one place too many
 * for it to drift.
 */
export const formatAmountInEuros = (amountCents: number) =>
  (amountCents / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

/**
 * Above the counters rather than behind a tooltip: the available figure drops on
 * its own once a year has passed, and no debit shows anywhere. Left unsaid, that
 * drop is a support ticket. The second sentence says what they are for: the
 * counters name a currency the employee has nowhere else, and a deadline with
 * no use attached would only tell him what he stands to lose.
 */
export const PURCHASE_POINTS_VALIDITY_NOTICE = "Vos yeaties sont valables un an après leur acquisition. Une fois disponibles, ils peuvent être convertis en chèque cadeau utilisable dans la boutique Yeati.";

/**
 * Said plainly, because a counter that moves on its own without explanation is
 * what generates support tickets.
 */
export const PURCHASE_POINTS_PENDING_EXPLAINER = "Ces yeaties vous seront acquis une fois votre achat confirmé par l'enseigne.";

export const PURCHASE_POINTS_BALANCE_ERROR_MESSAGE = "Vos yeaties n'ont pas pu être chargés.";

/**
 * Distinct from a balance of zero: an employee with no identity at the provider
 * has no counter at all, and showing them two zeros would read as a loss.
 */
export const PURCHASE_POINTS_NOT_ONBOARDED_MESSAGE = "Réglez un achat avec votre carte pour commencer à gagner des yeaties.";

// Label of the link leading away from the counters and histories, towards
// where yeaties are actually earned.
export const PURCHASE_POINTS_BRANDS_LINK_LABEL = "Où gagner des yeaties ?";

export const REWARD_HISTORY_TITLE = "Mes gains";
export const REWARD_HISTORY_EMPTY_MESSAGE = "Vous n'avez pas encore gagné de yeaties.";

/**
 * Distinct from an empty history: the page requested no longer exists, and the
 * message has to send the employee back rather than let them conclude their
 * gains vanished.
 */
export const REWARD_HISTORY_BEYOND_LAST_PAGE_MESSAGE = "Cette page ne contient aucun gain.";
export const REWARD_HISTORY_ERROR_MESSAGE = "Vos gains n'ont pas pu être chargés.";
export const REWARD_HISTORY_UNKNOWN_BRAND = "Enseigne inconnue";

/**
 * A cancelled gain stays in the list with its label: it left the counters, and
 * that departure has to be explained rather than being a silent gap.
 */
export const REWARD_STATUS_LABELS = {
  pending: "En attente",
  acquired: "Acquis",
  cancelled: "Annulé",
} as const;

/**
 * Two wordings rather than a date alone: yeaties leaving the available counter
 * must be explained on the line that earned them, exactly as a cancelled gain's
 * departure is. Their yeaties stay shown, a gain evaporating in silence being
 * worse than a total that no longer matches the list.
 */
export const REWARD_VALIDITY_PREFIX = "Valables jusqu'au";
export const REWARD_EXPIRED_PREFIX = "Périmés le";

/**
 * Prefix of a brand-level rate: `max_points_per_euro` is its best case, a real
 * offer may earn less. The conditional wording is therefore not a matter of
 * style, it is what tells the announced maximum from a promise.
 */
export const MAX_POINTS_RATE_PREFIX = "Jusqu'à";
export const POINTS_RATE_UNIT_LABEL = "yeaties";
export const POINTS_RATE_SPEND_LABEL = "par € dépensé";

// Shared by the empty state and the error state: a page with no way out is
// always left through the same door, with the same words.
export const BACK_TO_FIRST_PAGE_LABEL = "Revenir à la première page";

// Distinct from a technical outage: nothing is broken, the provider's own
// campaign targeting simply left nothing to show for this employee.
export const BRAND_OFFERS_EMPTY_MESSAGE = "Aucune offre disponible pour vous chez cette enseigne pour le moment.";

export const BACK_TO_BRANDS_LABEL = "Retour aux enseignes";
// Heading of a brand page until its brand is read, or when it cannot be.
export const BRAND_PAGE_FALLBACK_TITLE = "Offres de l'enseigne";

/**
 * The backend recomputes the employee's entitlements on every call: freezing
 * the result any longer in the browser would delay taking a loss of rights into
 * account. Retention is also shorter than the global default, this data being
 * personal.
 */
export const PURCHASE_POINTS_CACHE_CONFIG = {
  staleTime: 60_000,
  gcTime: 5 * 60_000,
  retry: false,
  refetchOnWindowFocus: false,
} as const;

/**
 * Ceiling of the wait imposed before a manual retry. A longer `Retry-After`
 * would overflow the range of `setTimeout`, which would then fire immediately;
 * past an hour the session has changed context anyway and another attempt still
 * makes sense.
 */
export const MAX_RETRY_DELAY_SECONDS = 3600;

/**
 * States the wait instead of leaving it to be guessed. No duration announced:
 * the timer only yields a boolean, and a frozen number that never counts down
 * would grow stale under the employee's eyes.
 */
export const RETRY_DELAY_NOTICE = "Le service demande un délai avant une nouvelle tentative.";

/**
 * Wording of its own for the availability badge, distinct from a plain channel
 * name: it says where the offer applies, and the "both" case has no matching
 * channel label.
 */
export const CHANNEL_AVAILABILITY_LABELS = {
  online: "Internet uniquement",
  offline: "En magasin uniquement",
  both: "Internet et magasin",
} as const;

/**
 * The channel type only binds compilation: the provider's response is not
 * validated at runtime. A channel outside the contract is dropped rather than
 * rendered without a label.
 */
export const isKnownChannel = (channel: string): channel is PurchasePointsChannel =>
  channel === "online" || channel === "offline";

/**
 * Deliberately narrower than the default allowlist: `a` would open a merchant
 * redirect the API does not expose, headings would break the page's heading
 * hierarchy, and generic containers bring nothing to textual content.
 */
export const PURCHASE_POINTS_ALLOWED_TAGS = ["p", "br", "strong", "em", "ul", "ol", "li"];

/**
 * Said from the bounds the provider sent, never invented: an offer with no end
 * is not "sans limite", it simply states none.
 */
export const OFFER_VALIDITY_LABELS = {
  between: (startDate: string, endDate: string) => `Du ${startDate} au ${endDate}`,
  since: (startDate: string) => `Depuis le ${startDate}`,
  until: (endDate: string) => `Jusqu'au ${endDate}`,
} as const;

export const PURCHASE_POINTS_LEGAL_NOTICE = "Les conditions de l'offre et les contrôles habituels de votre carte s'appliquent.";

export const OFFER_CATALOGUE_EMPTY_MESSAGE = "Aucune offre n'est disponible pour le moment.";
export const BEYOND_LAST_PAGE_MESSAGE = "Cette page ne contient aucune offre.";

// The discovery catalogue is filtered by voucher, like the personal one: an
// empty list is therefore not an outage, just the absence of a usable voucher
// for now.
export const BRAND_CATALOGUE_EMPTY_MESSAGE = "Aucune enseigne ne correspond à vos dotations actuelles.";

export const BRAND_CATALOGUE_NOTICE = "Ces enseignes font partie du programme. Les montants affichés sont les maximums proposés, pas une offre garantie.";

export const BRAND_SEARCH_QUERY_PARAM = "q";
export const NO_BRAND_SEARCH_TERM = "";
// Mirrors NAME_SEARCH_MAX_LENGTH of yeati-api: a longer term would be cut there anyway.
export const BRAND_SEARCH_MAX_LENGTH = 100;
export const BRAND_SEARCH_DEBOUNCE_MS = 300;
export const BRAND_SEARCH_LABEL = "Rechercher une enseigne";
export const BRAND_SEARCH_CLEAR_LABEL = "Effacer la recherche";
export const buildBrandCountLabel = (count: number) => `${count} enseigne${count > 1 ? "s" : ""}`;
export const buildBrandSearchNoMatchMessage = (searchTerm: string) => `Aucune enseigne ne correspond à « ${searchTerm} ».`;

export const HIGHLIGHTED_OFFERS_TITLE = "À la une";
export const BRAND_LIST_TITLE = "Nos enseignes partenaires";
export const HIGHLIGHTED_OFFERS_ERROR_MESSAGE = "Les offres à la une n'ont pas pu être chargées.";
export const HIGHLIGHTED_OFFERS_PAUSE_LABEL = "Mettre en pause le défilement des offres à la une";
export const HIGHLIGHTED_OFFERS_RESUME_LABEL = "Reprendre le défilement des offres à la une";

export const PURCHASE_POINTS_CONVERTED_LABEL = "Utilisés";
export const PURCHASE_POINTS_CONVERT_CTA_LABEL = "Utiliser mes yeaties";
export const PURCHASE_POINTS_CONVERT_DIALOG_TITLE = "Conversion de mes yeaties";
export const PURCHASE_POINTS_CONVERT_RECAP = "Vos yeaties disponibles vont être convertis en chèque utilisable dans la boutique.";
export const PURCHASE_POINTS_CONVERT_TERMS_NOTICE = "Ce chèque sera valable un an et cette conversion est définitive.";
export const PURCHASE_POINTS_CHECK_VALIDITY_NOTICE = "Votre chèque sera valable un an.";
export const PURCHASE_POINTS_CONVERT_CANCEL_LABEL = "Annuler";
export const PURCHASE_POINTS_CONVERT_CONFIRM_LABEL = "Confirmer la conversion";
export const PURCHASE_POINTS_CONVERT_PENDING_LABEL = "Conversion en cours…";
export const PURCHASE_POINTS_CONVERT_DONE_LABEL = "Fermer";
export const PURCHASE_POINTS_CONVERT_SUCCESS_TITLE = "Yeaties convertis";
export const PURCHASE_POINTS_CONVERT_SUCCESS_MESSAGE = "Votre chèque est disponible dès maintenant dans la boutique.";
export const PURCHASE_POINTS_CONVERT_ERROR_MESSAGE = "La conversion n'a pas pu aboutir. Réessayez dans un instant.";

// Not a failure: the balance simply moved since the screen was drawn.
export const PURCHASE_POINTS_BALANCE_CHANGED_MESSAGE = "Votre solde a changé, il n'y a plus de yeaties à convertir.";

export const CONVERSION_HISTORY_TITLE = "Mes conversions";
export const CONVERSION_HISTORY_EMPTY_MESSAGE = "Vous n'avez pas encore converti de yeaties.";
export const CONVERSION_HISTORY_ERROR_MESSAGE = "Vos conversions n'ont pas pu être chargées.";
export const CONVERSION_HISTORY_BEYOND_LAST_PAGE_MESSAGE = "Cette page ne contient aucune conversion.";
export const CONVERSION_CHECK_VALIDITY_PREFIX = "Utilisable jusqu'au";

export const YEATIES_CONSENT_NEW_TAB_HINT = "(s'ouvre dans un nouvel onglet)";

// The question is gone once it fails: point to where he can still answer.
export const YEATIES_CONSENT_SAVE_ERROR = {
  title: "Votre choix pour Mes Yeaties n'a pas pu être enregistré.",
  description: "Vous pouvez l'indiquer depuis Mon profil › Autorisations.",
} as const;

// Said to an employee still undecided while his works council's run is yet to
// come: silence counts as yes until then, and he must be told rather than find
// out when the programme starts.
export const YEATIES_AUTOMATIC_ACTIVATION_NOTICE = "Mes Yeaties s'activera automatiquement pour vous prochainement.";
export const YEATIES_EARLY_OPT_IN_LABEL = "En profiter dès maintenant";
